import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import BackButton from '../components/BackButton';
import GestionDocuments from '../components/GED/GestionDocuments';
import Messagerie from '../components/GED/Messagerie';
import { FileText, CreditCard, AlertCircle, Download, User, Calendar, CheckCircle, Clock, LogOut, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '../services/tokenService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BeneficiaireParticulierDashboard() {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [souscriptions, setSouscriptions] = useState([]);
  const [statistiques, setStatistiques] = useState({
    souscriptions_actives: 0,
    paiements_en_attente: 0,
    dommages_en_cours: 0
  });

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
    window.location.href = '/';
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = getToken();
      
      // Récupérer le profil
      const profileResponse = await fetch(`${API_URL}/api/beneficiaires-particuliers/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (profileResponse.ok) {
        const profileData = await profileResponse.json();
        setProfile(profileData);
      }

      // Récupérer les souscriptions
      const souscriptionsResponse = await fetch(`${API_URL}/api/souscriptions/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (souscriptionsResponse.ok) {
        const souscriptionsData = await souscriptionsResponse.json();
        // Trier du plus récent au plus ancien
        const sortedData = (souscriptionsData || []).sort((a, b) => new Date(b.date_creation || b.created_at || 0) - new Date(a.date_creation || a.created_at || 0));
        setSouscriptions(sortedData);
        
        // Calculer les statistiques
        setStatistiques({
          souscriptions_actives: souscriptionsData.filter(s => s.is_active).length,
          paiements_en_attente: 0, // À implémenter
          dommages_en_cours: 0 // À implémenter
        });
      }

    } catch {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      incomplete: { label: 'Incomplet', variant: 'secondary', icon: Clock },
      pending: { label: 'En attente', variant: 'warning', icon: Clock },
      validated: { label: 'Validé', variant: 'success', icon: CheckCircle },
      rejected: { label: 'Rejeté', variant: 'destructive', icon: AlertCircle }
    };

    const config = statusConfig[status] || statusConfig.incomplete;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coppam-blue mx-auto"></div>
          <p className="mt-4 text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* En-tête avec boutons */}
      <header className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <BackButton to="/" label="Accueil" />
              <div>
                <h1 className="text-2xl font-bold text-coppam-blue">Espace Bénéficiaire Particulier</h1>
                <p className="text-sm text-slate-600">
                  Bienvenue, <span className="font-semibold text-coppam-gold">{user?.prenom} {user?.nom}</span>
                </p>
              </div>
            </div>
            <Button onClick={handleLogout} variant="outline" data-testid="logout-btn">
              <LogOut className="h-4 w-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">

        {/* Statut du profil */}
        {profile && profile.status !== 'validated' && (
          <Card className="mb-6 border-l-4 border-l-coppam-gold bg-coppam-light-blue">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="h-6 w-6 text-coppam-gold flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="font-semibold text-coppam-blue mb-2">
                    Complétez votre profil
                  </h3>
                  <p className="text-sm text-slate-600 mb-3">
                    {profile.status === 'incomplete' && 
                      'Votre profil est incomplet. Veuillez compléter vos informations pour accéder aux dispositifs.'}
                    {profile.status === 'pending' && 
                      'Votre profil est en cours de validation par nos équipes. Vous serez notifié par email dès la validation.'}
                    {profile.status === 'rejected' && 
                      `Votre profil a été rejeté. Raison: ${profile.rejection_reason || 'Non spécifiée'}`}
                  </p>
                  {profile.status === 'incomplete' && (
                    <Button size="sm" className="bg-coppam-gold hover:bg-coppam-light-gold">
                      Compléter mon profil
                    </Button>
                  )}
                </div>
                {getStatusBadge(profile.status)}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cartes statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="border-l-4 border-l-coppam-blue">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-slate-600">
                  Souscriptions Actives
                </CardTitle>
                <FileText className="h-5 w-5 text-coppam-blue" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-coppam-gold">
                {statistiques.souscriptions_actives}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Dispositifs en cours
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-coppam-gold">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-slate-600">
                  Paiements en attente
                </CardTitle>
                <CreditCard className="h-5 w-5 text-coppam-gold" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-coppam-gold">
                {statistiques.paiements_en_attente}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                À régler
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-coppam-blue">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-slate-600">
                  Dommages en cours
                </CardTitle>
                <AlertCircle className="h-5 w-5 text-coppam-blue" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-coppam-gold">
                {statistiques.dommages_en_cours}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                En traitement
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Section Principale avec Tabs */}
        <Card className="mb-8">
          <CardContent className="p-6">
            {/* Tabs Navigation */}
            <div className="border-b border-slate-200 mb-6">
              <nav className="-mb-px flex space-x-4 md:space-x-8">
                <button className="border-b-2 border-coppam-gold py-4 px-1 text-sm font-medium text-coppam-blue">
                  📋 Mes Souscriptions
                </button>
              </nav>
            </div>

            {/* Contenu Souscriptions */}
            <div>
              <h3 className="text-lg font-semibold text-coppam-blue mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Mes Souscriptions
              </h3>
              <p className="text-sm text-slate-600 mb-4">
                Vos dispositifs actifs et en cours
              </p>
              {souscriptions.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Aucune souscription pour le moment</p>
                  <Button className="mt-4 bg-coppam-gold hover:bg-coppam-light-gold">
                    Découvrir les dispositifs
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {souscriptions.slice(0, 3).map((souscription) => (
                    <div
                      key={souscription.id}
                      className="p-4 border border-slate-200 rounded-lg hover:border-coppam-blue transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-coppam-blue">
                            {souscription.dispositif?.nom || 'Dispositif'}
                          </h4>
                          <p className="text-xs text-slate-500">
                            Réf.Solidaire: {souscription.numero_police}
                          </p>
                        </div>
                        <Badge variant={souscription.is_active ? 'success' : 'secondary'}>
                          {souscription.is_active ? 'Actif' : 'Inactif'}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between text-xs text-slate-600">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Depuis {new Date(souscription.created_at).toLocaleDateString()}
                        </span>
                        <Button size="sm" variant="outline">
                          Voir détails
                        </Button>
                      </div>
                    </div>
                  ))}
                  {souscriptions.length > 3 && (
                    <Button variant="ghost" className="w-full text-coppam-blue">
                      Voir toutes les souscriptions ({souscriptions.length})
                    </Button>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Documents & Messagerie Section */}
        <div className="grid grid-cols-1 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-coppam-blue flex items-center gap-2">
                <Download className="h-5 w-5" />
                Gestion de Documents (GED)
              </CardTitle>
              <CardDescription>
                Téléchargez et gérez vos documents et justificatifs
              </CardDescription>
            </CardHeader>
            <CardContent>
              <GestionDocuments />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-coppam-blue flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Messagerie Sécurisée
              </CardTitle>
              <CardDescription>
                Communiquez avec l'administration et vos COOPER'S
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Messagerie />
            </CardContent>
          </Card>
        </div>

        {/* Mon Profil */}
        {profile && (
          <Card>
            <CardHeader>
              <CardTitle className="text-coppam-blue flex items-center gap-2">
                <User className="h-5 w-5" />
                Mes Informations
              </CardTitle>
              <CardDescription>
                Vos coordonnées personnelles
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-slate-600 block mb-1">
                    Nom complet
                  </label>
                  <p className="text-slate-900">
                    {profile.prenom} {profile.nom}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600 block mb-1">
                    Email
                  </label>
                  <p className="text-slate-900">{profile.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600 block mb-1">
                    Téléphone
                  </label>
                  <p className="text-slate-900">{profile.telephone}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600 block mb-1">
                    Adresse
                  </label>
                  <p className="text-slate-900">
                    {profile.adresse || 'Non renseignée'}
                    {profile.code_postal && profile.ville && (
                      <><br />{profile.code_postal} {profile.ville}</>
                    )}
                  </p>
                </div>
                {profile.date_naissance && (
                  <div>
                    <label className="text-sm font-medium text-slate-600 block mb-1">
                      Date de naissance
                    </label>
                    <p className="text-slate-900">
                      {new Date(profile.date_naissance).toLocaleDateString()}
                    </p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-slate-600 block mb-1">
                    Statut du profil
                  </label>
                  <div className="mt-1">
                    {getStatusBadge(profile.status)}
                  </div>
                </div>
              </div>
              <div className="mt-6">
                <Button variant="outline" className="text-coppam-blue border-coppam-blue hover:bg-coppam-light-blue">
                  <User className="h-4 w-4 mr-2" />
                  Modifier mes informations
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
